'use client';

import { ReactNode } from 'react';
import Box from '@mui/material/Box';

import Background from '@/components/atoms/Background/Background';
import { Icon } from '@/components/atoms/Icon/Icon';

interface InlineTextProps {
  $primary?: boolean;
  $secondary?: boolean;
  $light?: boolean;
  $disabled?: boolean;
  $transform?: string;
  $noWrap?: boolean;
  children?: ReactNode;
}
export const InlineText = ({
  $primary,
  $secondary,
  $light,
  $disabled,
  $transform,
  $noWrap,
  children,
}: InlineTextProps) => (
  <Box
    component='span'
    sx={{
      m: 0,
      ...($primary && { color: '#f18b6c' }),
      ...($secondary && { color: '#000000' }),
      ...($light && { color: '#f2ede3' }),
      ...($disabled && { color: '#757575' }),
      textTransform: $transform || 'initial',
      ...($noWrap && { whiteSpace: 'nowrap' }),
    }}
  >
    {children}
  </Box>
);

export const LargeIcon = (props: any) => (
  <Icon {...props} fontSize={64} color='#000000' />
);

export const Container = ({ children }: { children?: ReactNode }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#faf7f3',
    }}
  >
    {children}
  </Box>
);

export const renderIf = (enabled?: any) => {
  return ({ children }: { children: ReactNode }) => (
    <>{enabled ? children : null}</>
  );
};

export const Picture = ({ children }: { children?: ReactNode }) => (
  <picture>{children}</picture>
);

interface ImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  $blurUpSrc?: string;
}
export const Img = ({ $blurUpSrc, style, ...props }: ImgProps) => (
  <Box
    component='img'
    {...props}
    sx={{
      objectFit: 'cover',
      objectPosition: 'center',
      width: '100%',
      ...($blurUpSrc && {
        backgroundImage: `url('${$blurUpSrc}')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }),
      '&:focus': { outlineColor: '#f18b6c' },
    }}
  />
);

interface ImageSource {
  src?: string;
  minWidthPx?: number | string;
}
interface ImageProps {
  sources?: ImageSource[];
  fallback?: string;
  alt?: string;
  className?: string;
  blurUpSrc?: string;
  tabIndex?: number;
  onClick?: () => void;
  onKeyDown?: (ev: any) => void;
}
export const Image = ({
  sources = [],
  fallback,
  alt,
  className,
  blurUpSrc,
  tabIndex,
  onClick = () => {},
  onKeyDown = () => {},
}: ImageProps) => {
  return (
    <Picture>
      {sources.map(({ src, minWidthPx }) => (
        <source
          key={src}
          srcSet={src}
          media={minWidthPx ? `(min-width: ${minWidthPx}px)` : undefined}
        />
      ))}
      <Img
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        onClick={onClick}
        src={fallback}
        alt={alt}
        className={className}
        $blurUpSrc={blurUpSrc}
        loading='lazy'
      />
    </Picture>
  );
};

export const FullImage = ({ className, ...props }: any) => (
  <Box
    sx={{
      '& img': {
        height: '20rem',
        '@media (max-width: 959px)': { height: '10rem' },
      },
      cursor: 'zoom-in',
    }}
  >
    <Image className={className} {...props} />
  </Box>
);

export const LightboxImage = Image;

export const Thumbnail = ({ alt, style, ...props }: any) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      '& > *': { width: '100%', height: '100%' },
    }}
  >
    <Background {...props} />
  </Box>
);

export const ThumbnailContainer = ({
  children,
  $isLast,
  ...props
}: {
  children?: ReactNode;
  $isLast?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <Box
    component='button'
    {...props}
    sx={{
      cursor: 'pointer',
      p: 0,
      backgroundColor: 'transparent',
      border: 'none',
      mr: $isLast ? 0 : '1rem',
      outlineColor: '#f18b6c',
      height: '5rem',
      width: '5rem',
      minWidth: '5rem',
      '@media (max-width: 959px)': {
        height: '4rem',
        width: '4rem',
        minWidth: '4rem',
      },
    }}
  >
    {children}
  </Box>
);

export const ThumbnailRow = ({ children }: { children?: ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'row', overflowX: 'auto' }}>
    {children}
  </Box>
);

export const FullImageContainer = ({ children }: { children?: ReactNode }) => (
  <Box sx={{ position: 'relative' }}>{children}</Box>
);

export const NextPrevIcon = ({ ...props }: any) => (
  <Box sx={{ fontSize: '2rem', filter: 'drop-shadow(4px 2px 2px #000000)' }}>
    <Icon {...props} />
  </Box>
);

export const PreviousButton = ({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: ReactNode;
}) => (
  <Box
    component='button'
    {...props}
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      pr: '5rem',
      width: '7.5rem',
      color: '#f2ede3',
      outlineColor: '#f18b6c',
      border: 'none',
      cursor: 'pointer',
      background: 'none',
      '&:hover': {
        background: 'linear-gradient(to left, transparent, #000000)',
      },
    }}
  >
    {children}
  </Box>
);

export const NextButton = ({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: ReactNode;
}) => (
  <Box
    component='button'
    {...props}
    sx={{
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      width: '7.5rem',
      pl: '5rem',
      color: '#f2ede3',
      outlineColor: '#f18b6c',
      border: 'none',
      cursor: 'pointer',
      background: 'none',
      '&:hover': {
        background: 'linear-gradient(to right, transparent, #000000)',
      },
    }}
  >
    {children}
  </Box>
);

export const InvisibleLink = ({
  children,
  tabIndex,
  ...props
}: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'tabIndex'> & {
  children?: ReactNode;
  tabIndex?: string | number;
}) => (
  <Box
    component='a'
    {...props}
    tabIndex={tabIndex != null ? Number(tabIndex) : undefined}
    sx={{
      position: 'absolute',
      opacity: 0,
      '&:focus': { opacity: 1, zIndex: 1 },
    }}
  >
    {children}
  </Box>
);
